import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoDocumentTextOutline } from "react-icons/io5";
import { HiOutlinePhotograph } from "react-icons/hi";
import { imgUrl } from "../config/api";
import GetMeasurementArrayPanel from "../Functions/GetMeasurementArrayPanel";
import { ReactComponent as Logoen } from "../Images/public/logoen.svg";
import { ReactComponent as Logofa } from "../Images/public/logofa.svg";
import { DEMO_PRODUCTS_MULTI, DEMO_PRODUCTS_SINGLE, DEMO_PRODUCTS_READYMADE } from "./OrdersDemoData";
import OrderProductDetailsByModel, {
  DetailRow,
  DetailSection,
  formatQuantity,
  joinParts,
} from "./OrderProductDetailsByModel";

const DEMO_ORDERS = {
  "0321": {
    sewingId_fa: "۷۶۸۲۳",
    sewingId_en: "76823",
    measurementId_fa: "۸۵۵۲۷",
    measurementId_en: "85527",
    orderTaker_fa: "فریدی - ۷۱۰۹",
    orderTaker_en: "Faridi - 7109",
    orderDate_fa: "۱۴۰۵/۰۶/۲۸ ۱۸:۲۹",
    orderDate_en: "2026/09/19 18:29",
    measurementDate_fa: "۱۴۰۵/۰۶/۲۸ ۱۸:۲۹",
    measurementDate_en: "2026/09/19 18:29",
    measurer_fa: "آقای رضایی - ۴۲۰۸",
    measurer_en: "Mr. Reza - 4208",
    invoiceNumber_fa: "۲۰۳۴۶۱",
    invoiceNumber_en: "203461",
    customerId_fa: "۳۸۴۹۸",
    customerId_en: "38498",
    customerName_fa: "خانم اشرفی",
    customerName_en: "Ms. Ashrafi",
    phone_fa: "۰۹۱۲۷۱۲۱۱۶۰",
    phone_en: "09127121160",
    products: DEMO_PRODUCTS_SINGLE,
  },
  "0099": {
    sewingId_fa: "۴۵۱۰۲",
    sewingId_en: "45102",
    measurementId_fa: "۶۰۲۱۸",
    measurementId_en: "60218",
    orderTaker_fa: "احمدی - ۴۲۰۱",
    orderTaker_en: "Ahmadi - 4201",
    orderDate_fa: "۱۴۰۵/۰۵/۱۵ ۱۴:۱۰",
    orderDate_en: "2026/08/06 14:10",
    measurementDate_fa: "۱۴۰۵/۰۵/۱۵ ۱۴:۱۰",
    measurementDate_en: "2026/08/06 14:10",
    measurer_fa: "آقای رضا - ۴۲۰۸",
    measurer_en: "Mr. Reza - 4208",
    invoiceNumber_fa: "۱۹۸۷۷۴",
    invoiceNumber_en: "198774",
    customerId_fa: "۲۲۱۵۰",
    customerId_en: "22150",
    customerName_fa: "آقای رضایی",
    customerName_en: "Mr. Rezaei",
    phone_fa: "۰۹۱۲۱۲۳۴۵۶۷",
    phone_en: "09121234567",
    products: DEMO_PRODUCTS_MULTI,
  },
  "9999": {
    sewingId_fa: "۱۰۰۰۱",
    sewingId_en: "10001",
    measurementId_fa: "۱۰۰۰۲",
    measurementId_en: "10002",
    orderTaker_fa: "محمدی - ۳۳۰۱",
    orderTaker_en: "Mohammadi - 3301",
    orderDate_fa: "۱۴۰۵/۰۴/۱۰ ۱۱:۱۵",
    orderDate_en: "2026/07/01 11:15",
    measurementDate_fa: "۱۴۰۵/۰۴/۱۰ ۱۱:۱۵",
    measurementDate_en: "2026/07/01 11:15",
    measurer_fa: "خانم نوری - ۳۳۱۰",
    measurer_en: "Ms. Nouri - 3310",
    invoiceNumber_fa: "۱۸۵۵۲۰",
    invoiceNumber_en: "185520",
    customerId_fa: "۱۵۰۲۲",
    customerId_en: "15022",
    customerName_fa: "خانم کریمی",
    customerName_en: "Ms. Karimi",
    phone_fa: "۰۹۱۲۹۸۷۶۵۴۳",
    phone_en: "09129876543",
    products: DEMO_PRODUCTS_READYMADE,
  },
};

function normalizeOrderNumber(value) {
  return String(value || "")
    .trim()
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d));
}

// فقط حرف اول بزرگ؛ بقیه همان‌طور که کاربر نوشته
function capitalizeFirstLetter(value) {
  const text = String(value ?? "");
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// تبدیل فیلدهای سفارش به نام‌هایی که GetMeasurementArrayPanel انتظار دارد
// (مثل cartValues در صفحات زبرا / پلیسه / پانچی)
function buildMeasurementCartValues(product) {
  if (!product) return {};

  const modelId = `${product.SewingModelId || ""}`;
  const isDrapery = modelId === "0099" || modelId === "0321";

  // Mount برای پرده لایه‌ای با سایه فرق دارد
  let mount = product.Mount;
  if (!mount && product.MountPositionId != null) {
    if (isDrapery) {
      const draperyMount = { 4801: "Ceiling", 4802: "Wall", 4803: "Moulding" };
      mount = draperyMount[product.MountPositionId] || null;
    } else if (product.MountPositionId === 4801) {
      mount = "Inside";
    } else {
      mount = "Outside";
    }
  }

  // IsWalled: "Wall" یا "Ceiling" (سایه)
  let isWalled = product.IsWalled;
  if (product.installationLocation === "Wall" || product.installationLocation === "Ceiling") {
    isWalled = product.installationLocation;
  } else if (isWalled === true || isWalled === "true") {
    isWalled = "Wall";
  } else if (isWalled === false || isWalled === "false") {
    isWalled = "Ceiling";
  }

  // Curtain Position / Finished Length Type از Id
  const curtainPositionMap = {
    4901: "Standard",
    4902: "Wall to Wall",
    4903: "Right Corner Window",
    4904: "Left Corner Window",
  };
  const finishedLengthMap = {
    5401: "Sill",
    5402: "Apron",
    5403: "Floor",
    5404: "Slight Puddle",
  };
  const curtainPosition =
    product.CurtainPosition || curtainPositionMap[product.CurtainPositionId] || null;
  const finishedLengthType =
    product.FinishedLengthType || finishedLengthMap[product.FinishedLengthTypeId] || null;

  // عرض پایه پنجره
  const baseWidth =
    product.Width3C ??
    product.Width3A ??
    product.Width ??
    product.Width1 ??
    product.WindowWidth ??
    null;
  // ارتفاع پایه پنجره
  const baseHeight =
    product.Height3C ??
    product.Height3E ??
    product.Height ??
    product.Height1 ??
    product.WindowHeight ??
    null;

  // Extension / Stack چپ و راست
  const extensionLeft = product.ExtensionLeft ?? product.LeftStackWidth ?? null;
  const extensionRight = product.ExtensionRight ?? product.RightStackWidth ?? null;

  // Mount height
  const shadeMount = product.ShadeMount ?? product.RodMountHeight ?? null;

  // اندازه‌گیری پیشرفته
  const calcMeasurements = product.calcMeasurements ?? product.CalcWindowSize ?? false;

  // میله نصب شده؟
  const hasRod = product.hasRod ?? product.RodInstalled ?? false;

  return {
    ...product,
    SewingModelId: product.SewingModelId,
    Mount: mount,
    IsWalled: isWalled,
    CurtainPosition: curtainPosition,
    FinishedLengthType: finishedLengthType,
    calcMeasurements: calcMeasurements,
    hasRod: hasRod,
    WindowWidth: product.WindowWidth ?? product.WidthCart ?? baseWidth,
    WindowHeight: product.WindowHeight ?? product.HeightCart ?? baseHeight,
    Width: product.Width ?? baseWidth,
    Height: product.Height ?? product.Height1 ?? baseHeight,
    Width3A: product.Width3A ?? baseWidth,
    Width3C: product.Width3C ?? baseWidth,
    Height3C: product.Height3C ?? baseHeight,
    Height3E: product.Height3E ?? baseHeight,
    Width1: product.Width1 ?? baseWidth,
    Width2: product.Width2,
    Width3: product.Width3,
    Height1: product.Height1 ?? baseHeight,
    Height2: product.Height2,
    Height3: product.Height3,
    ExtensionLeft: extensionLeft,
    ExtensionRight: extensionRight,
    ShadeMount: shadeMount,
    step31: product.step31,
    RodWidth: product.RodWidth,
    RodToFloor: product.RodToFloor,
    RodToBottom: product.RodToBottom,
    CeilingToWindow1: product.CeilingToWindow1 ?? product.Height1,
    CeilingToWindow2: product.CeilingToWindow2 ?? product.Height2,
    CeilingToWindow3: product.CeilingToWindow3 ?? product.Height3,
    CeilingToFloor1: product.CeilingToFloor1 ?? product.Height1,
    CeilingToFloor2: product.CeilingToFloor2 ?? product.Height2,
    CeilingToFloor3: product.CeilingToFloor3 ?? product.Height3,
    WindowToFloor: product.WindowToFloor ?? product.Height1,
  };
}

function MeasurementsBox({ product }) {
  const { i18n } = useTranslation();
  if (!product?.SewingModelId) return null;

  const cartValues = buildMeasurementCartValues(product);
  if (cartValues.WindowWidth == null && cartValues.WindowHeight == null) return null;

  const measurementKey = [
    i18n.language,
    product.SewingModelId,
    product.FabricId,
    cartValues.calcMeasurements ? "calc" : "simple",
    cartValues.Mount || "",
    cartValues.CurtainPosition || "",
    cartValues.FinishedLengthType || "",
    cartValues.IsWalled || "",
    cartValues.hasRod ? "rod" : "",
    cartValues.step31 || "",
    cartValues.RodWidth ?? "",
    cartValues.ExtensionLeft ?? "",
    cartValues.ExtensionRight ?? "",
    cartValues.ShadeMount ?? "",
    cartValues.Height1 ?? "",
    cartValues.Height2 ?? "",
    cartValues.Height3 ?? "",
    cartValues.WindowWidth,
    cartValues.WindowHeight,
  ].join("-");

  return (
    <div className="orders-measurements-wrap">
      <GetMeasurementArrayPanel
        key={measurementKey}
        modelId={`${product.SewingModelId}`}
        cartValues={cartValues}
      />
    </div>
  );
}

function ProductDetails({ product }) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language === "fa";
  // خط بالا تبدیل به fa بشه 
  const modelName = isFa ? product.ModelNameFa : product.ModelNameEn;
  const roomLabel = joinParts([
    isFa ? product.RoomNameFa : product.RoomNameEn,
    product.WindowDescription,
  ]);

  return (
    <div className="orders-product-details-en">
      {modelName && (
        <h2 className="orders-product-model">
          {isFa ? `${modelName} ${t("CUSTOM")}` : `${t("CUSTOM")} ${modelName}`}
        </h2>
      )}
      {roomLabel && <h2 className="orders-product-room-label">{roomLabel}</h2>}
      <MeasurementsBox product={product} />
      <OrderProductDetailsByModel product={product} />
      {product.moreInfoInput && (
        <DetailSection>
          <DetailRow
            label={t("Order Notes")}
            value={capitalizeFirstLetter(product.moreInfoInput)}
          />
        </DetailSection>
      )}
      {product.NeedInstall && (
        <DetailSection>
          <DetailRow label={t("INSTALLATION SERVICES")} value={t("Yes")} />
        </DetailSection>
      )}
      <ProductAttachments attachments={product.SewingOrderAttachments} />
      {product.Count != null && product.Count !== "" && (
        <div className="orders-product-qty">
          <span className="orders-product-label">{t("Quantity")}</span>
          <span className="orders-product-value">{formatQuantity(product.Count, isFa)}</span>
        </div>
      )}
    </div>
  );
}

function ProductDetailsReadyMade({ product }) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language === "fa";
  // خط بالا تبدیل به fa بشه 
  const cm = t("CM");
  const productName = isFa
    ? product.ProductFAName || product.ProductENName
    : product.ProductENName;
  const sizeLabel =
    (isFa ? product.ProductSizeFaName : product.ProductSizeEnName) ||
    joinParts(
      [
        product.Width != null ? `${product.Width}${cm}` : null,
        product.Length != null ? `${product.Length}${cm}` : null,
        product.Height != null ? `${product.Height}${cm}` : null,
        product.Diameter != null ? `Ø ${product.Diameter}${cm}` : null,
      ],
      " x "
    );

  return (
    <div className="orders-product-details-en">
      {productName && <h2 className="orders-product-model">{productName}</h2>}

      <DetailSection title={t("General Details")}>
        <DetailRow
          label={t("Design")}
          value={isFa ? product.DesignFaName || product.DesignEnName : product.DesignEnName}
        />
        <DetailRow
          label={t("Color")}
          value={isFa ? product.ColorFaName || product.ColorEnName : product.ColorEnName}
        />
        <DetailRow label={t("Size")} value={sizeLabel} />
        <DetailRow
          label={t("Model")}
          value={
            isFa
              ? product.SewingModelFaName || product.SewingModelEnName
              : product.SewingModelEnName
          }
        />
        <DetailRow
          label={t("Lining")}
          value={
            isFa
              ? product.LiningTypeFaName || product.LiningTypeEnName
              : product.LiningTypeEnName
          }
        />
      </DetailSection>

      {product.moreInfoInput && (
        <DetailSection>
          <DetailRow
            label={t("Order Notes")}
            value={capitalizeFirstLetter(product.moreInfoInput)}
          />
        </DetailSection>
      )}

      {product.NeedInstall && (
        <DetailSection>
          <DetailRow label={t("INSTALLATION SERVICES")} value={t("Yes")} />
        </DetailSection>
      )}

      <ProductAttachments attachments={product.SewingOrderAttachments} />

      {product.Count != null && product.Count !== "" && (
        <div className="orders-product-qty">
          <span className="orders-product-qty-label">{t("QTY")}</span>
          <span className="orders-product-qty-value">{formatQuantity(product.Count, isFa)}</span>
        </div>
      )}

      {!product.moreInfoInput && (
        <DetailSection>
          <DetailRow
            label={t("Order Notes")}
            value={capitalizeFirstLetter(
              isFa
                ? product.DescriptionFa || product.DescriptionEn || product.Description
                : product.DescriptionEn || product.Description
            )}
          />
        </DetailSection>
      )}
    </div>
  );
}

function getFileExtension(fileUrl = "", fileName = "") {
  const source = fileName || fileUrl;
  const match = source.match(/\.([a-z0-9]+)(?:\?|$)/i);
  return match ? match[1].toLowerCase() : "";
}

function getFileIcon(extension) {
  switch (extension) {
    case "pdf":
    case "doc":
    case "docx":
    case "txt":
    case "xls":
    case "xlsx":
      return <IoDocumentTextOutline className="orders-attachment-file-icon" />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
    case "bmp":
    case "svg":
    case "zip":
    case "rar":
      return <HiOutlinePhotograph className="orders-attachment-file-icon" />;
    default:
      return <IoDocumentTextOutline className="orders-attachment-file-icon" />;
  }
}

function ProductAttachments({ attachments }) {
  const { t } = useTranslation();
  if (!Array.isArray(attachments) || !attachments.length) return null;

  return (
    <DetailSection title={t("UPLOADED IMAGES/PDF")}>
      <div className="orders-attachments">
        {attachments.map((file, index) => {
          const fileUrl = imgUrl(file.FileUrl);
          const fileName = file.UserFileName || `${t("Upload Image")} ${index + 1}`;
          const extension = getFileExtension(file.FileUrl, fileName);

          return (
            <a
              key={`${file.FileUrl}-${index}`}
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="orders-attachment-item"
              title={fileName}
            >
              {getFileIcon(extension)}
              <span className="orders-attachment-name">{fileName}</span>
            </a>
          );
        })}
      </div>
    </DetailSection>
  );
}

function OrderProductItem({ product, index, totalCount }) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language === "fa";
  // خط بالا تبدیل به fa بشه 
  const isReadyMade = Boolean(product.ProductId);
  const showNumber = totalCount > 1;
  const altName =
    (isFa
      ? product.ProductFAName || product.ModelNameFa
      : product.ProductENName || product.ModelNameEn) || t("orders");

  return (
    <div className="orders-product-item">
      {showNumber && (
        <span className="orders-product-number">
          {t("order_item_no")} {isFa ? t(String(index + 1)) : index + 1}
        </span>
      )}
      <div className="orders-product-photo">
        <img src={imgUrl(product.PhotoUrl)} alt={altName} />
      </div>
      <div className="orders-product-info">
        {isReadyMade ? (
          <ProductDetailsReadyMade product={product} />
        ) : (
          <ProductDetails product={product} />
        )}
      </div>
    </div>
  );
}

function OrdersPage() {
  const { t, i18n } = useTranslation();
  const [orderNumber, setOrderNumber] = useState("");
  const [searchedOrder, setSearchedOrder] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const printAreaRef = useRef(null);
  const isRTL = i18n.language === "fa";
  // خط بالا تبدیل به fa بشه 
  const lang = isRTL ? "fa" : "en";
  // خط بالا قسمت اول تبدیل به fa  بشه
  const canPrint = Boolean(searchedOrder?.products?.length);

  const getField = (order, key) => order[`${key}_${lang}`];

  const handleSearch = (e) => {
    e.preventDefault();
    const key = normalizeOrderNumber(orderNumber);
    setHasSearched(true);
    setSearchedOrder(DEMO_ORDERS[key] || null);
  };

  const handlePrint = () => {
    if (!canPrint || !printAreaRef.current) return;
    document.body.classList.add("orders-printing");
    const cleanup = () => {
      document.body.classList.remove("orders-printing");
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    window.print();
  };

  return (
    <div className={`orders-page ${isRTL ? "font_farsi" : "font_en"}`}>
      <form className="orders-search-form" onSubmit={handleSearch}>
        <div className="orders-search-row">
          <div className="group-page-input-container orders-input-wrap">
            <label className={isRTL ? "group-page-label-fa" : "group-page-label"}>
              {t("enter_order_number")}
            </label>
            <input
              type="text"
              className="group-page-input"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder={t("enter_order_number")}
            />
          </div>
          <button type="submit" className="orders-search-btn">
            {t("search")}
          </button>
          {canPrint && (
            <button type="button" className="orders-print-btn" onClick={handlePrint}>
              {t("print_invoice")}
            </button>
          )}
        </div>
      </form>

      {hasSearched && !searchedOrder && (
        <p className="orders-not-found">{t("order_not_found")}</p>
      )}

      {searchedOrder && (
        <div className="orders-print-area" ref={printAreaRef}>
          <div className="orders-invoice-header" dir={isRTL ? "rtl" : "ltr"}>
            <div className="orders-invoice-brand">
              {isRTL ? (
                <Logofa className="orders-invoice-logo" aria-label="Atlas Pood" />
              ) : (
                <Logoen className="orders-invoice-logo" aria-label="Atlas Pood" />
              )}
            </div>

            <div className="orders-invoice-meta-grid">
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("sewing_id")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "sewingId")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("order_date")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "orderDate")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("order_taker")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "orderTaker")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("measurement_id")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "measurementId")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("measurer")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "measurer")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("measurement_date")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "measurementDate")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("invoice_number")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "invoiceNumber")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("customer_id")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "customerId")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("customer_name")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "customerName")}
                </span>
              </div>
              <div className="orders-invoice-meta-item">
                <span className="orders-invoice-meta-label">{t("phone")}</span>
                <span className="orders-product-room-label">
                  {getField(searchedOrder, "phone")}
                </span>
              </div>
            </div>
          </div>

          {searchedOrder.products?.length > 0 && (
            <div className="orders-products" dir={isRTL ? "rtl" : "ltr"}>
              {searchedOrder.products.map((product, index) => (
                <OrderProductItem
                  key={`${product.ProductId || product.SewingModelId}-${product.FabricId || ""}-${product.WidthCart || product.Width || ""}-${index}`}
                  product={product}
                  index={index}
                  totalCount={searchedOrder.products.length}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
